import { useState, useEffect, useCallback } from 'react';
import { useSearchParams } from 'react-router-dom';
import { motion } from 'framer-motion';
import { stagger, fadeUp } from '@/lib/animations';
import { DollarSign, AlertTriangle, FileText, BarChart3 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '@/components/ui/tabs';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { toast } from 'sonner';
import { PageHeader, MetricCard, KPIGrid, DataTable, type Column } from '@/components/common';
import {
  rateAnalyticsApi,
  RateAnalyticsSummary,
  RatesByCarrier,
  RatesByLane,
  ExpiringRate,
  RateTrendData,
  TransportMode,
} from '@/api/services';
import { getErrorMessage } from '@/api/client';
import {
  PieChart,
  Pie,
  Cell,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  Legend,
  Area,
  AreaChart,
} from 'recharts';
import { formatDate } from '@/lib/utils';
import { formatCurrency as formatMoney } from '@/lib/utils';

// Chart colors - using CSS variables
const MODE_COLORS: Record<TransportMode, string> = {
  TruckFTL: 'var(--color-primary-500)',
  TruckLTL: 'var(--color-primary-400)',
  Ocean: 'var(--color-success-500)',
  Air: 'var(--color-purple-500)',
  Rail: 'var(--color-warning-500)',
  Intermodal: 'var(--color-primary-600)',
};

const COLORS = [
  'var(--color-primary-500)',
  'var(--color-success-500)',
  'var(--color-purple-500)',
  'var(--color-warning-500)',
  'var(--color-danger-500)',
  'var(--color-primary-600)',
  'var(--color-teal-500)',
  'var(--color-pink-500)',
];

const EXPIRY_DAYS = [
  { value: 7, label: '7 Days' },
  { value: 14, label: '14 Days' },
  { value: 30, label: '30 Days' },
  { value: 60, label: '60 Days' },
];

export default function RateAnalytics() {
  const [searchParams, setSearchParams] = useSearchParams();
  const [loading, setLoading] = useState(true);

  // Data state
  const [summary, setSummary] = useState<RateAnalyticsSummary | null>(null);
  const [carrierStats, setCarrierStats] = useState<RatesByCarrier[]>([]);
  const [laneStats, setLaneStats] = useState<RatesByLane[]>([]);
  const [expiringRates, setExpiringRates] = useState<ExpiringRate[]>([]);
  const [trends, setTrends] = useState<RateTrendData[]>([]);

  // Filters
  const activeTab = searchParams.get('tab') || 'carrier';
  const expiryDays = parseInt(searchParams.get('days') || '30') as 7 | 14 | 30 | 60;
  const modeFilter = searchParams.get('mode') as TransportMode | undefined;

  const setFilter = (key: string, value: string | undefined) => {
    const params = new URLSearchParams(searchParams);
    if (value) {
      params.set(key, value);
    } else {
      params.delete(key);
    }
    setSearchParams(params);
  };

  // Load initial data
  useEffect(() => {
    const loadSummary = async () => {
      try {
        const [summaryData, trendsData] = await Promise.all([
          rateAnalyticsApi.getSummary(),
          rateAnalyticsApi.getTrends({ months: 6 }),
        ]);
        setSummary(summaryData);
        setTrends(trendsData);
      } catch (error) {
        toast.error(getErrorMessage(error));
      }
    };

    loadSummary();
  }, []);

  // Load tab-specific data
  const loadTabData = useCallback(async () => {
    setLoading(true);
    try {
      if (activeTab === 'carrier') {
        const data = await rateAnalyticsApi.getByCarrier({ mode: modeFilter, limit: 20 });
        setCarrierStats(data);
      } else if (activeTab === 'lane') {
        const data = await rateAnalyticsApi.getByLane({ mode: modeFilter, limit: 20 });
        setLaneStats(data);
      } else if (activeTab === 'expiring') {
        const data = await rateAnalyticsApi.getExpiring(expiryDays);
        setExpiringRates(data);
      }
    } catch (error) {
      toast.error(getErrorMessage(error));
    } finally {
      setLoading(false);
    }
  }, [activeTab, modeFilter, expiryDays]);

  useEffect(() => {
    loadTabData();
  }, [loadTabData]);

  /**
   * Delegates to the shared formatter so the currency comes from the record
   * rather than from a hardcoded 'USD' in this file. A page-local formatter is
   * how an order billed in dirhams ends up rendered with a dollar sign.
   */
  const formatCurrency = (amount: number, currency = 'USD') => formatMoney(amount, currency);

  const formatNumber = (num: number) => {
    return new Intl.NumberFormat('en-US').format(num);
  };

  const carrierColumns: Column<RatesByCarrier>[] = [
    {
      key: 'carrierName',
      header: 'Carrier',
      sortable: true,
      render: (row) => (
        <div>
          <div className="font-medium">{row.carrierName}</div>
          <div className="text-xs leading-4 text-muted-foreground">{row.carrierCode}</div>
        </div>
      ),
    },
    {
      key: 'totalRates',
      header: 'Total Rates',
      sortable: true,
      className: 'text-right',
      render: (row) => <span className="font-medium">{formatNumber(row.totalRates)}</span>,
    },
    {
      key: 'activeRates',
      header: 'Active',
      sortable: true,
      className: 'text-right',
      render: (row) => (
        <Badge
          variant="outline"
          className="bg-status-success-subtle text-status-success-foreground"
        >
          {formatNumber(row.activeRates)}
        </Badge>
      ),
    },
    {
      key: 'avgRate',
      header: 'Avg Rate',
      sortable: true,
      className: 'text-right',
      render: (row) => (
        <span className="font-semibold text-status-success-foreground">
          {formatCurrency(row.avgRate)}
        </span>
      ),
    },
    {
      key: 'minRate',
      header: 'Min',
      sortable: true,
      className: 'text-right',
      render: (row) => <span className="text-muted-foreground">{formatCurrency(row.minRate)}</span>,
    },
    {
      key: 'maxRate',
      header: 'Max',
      sortable: true,
      className: 'text-right',
      render: (row) => <span className="text-muted-foreground">{formatCurrency(row.maxRate)}</span>,
    },
  ];

  const laneColumns: Column<RatesByLane>[] = [
    {
      key: 'originPort',
      header: 'Lane',
      sortable: true,
      render: (row) => (
        <div className="flex items-center gap-2">
          <Badge variant="outline">{row.originPort}</Badge>

          <Badge variant="outline">{row.destinationPort}</Badge>
        </div>
      ),
    },
    {
      key: 'rateCount',
      header: 'Rates',
      sortable: true,
      className: 'text-right',
      render: (row) => <span className="font-medium">{formatNumber(row.rateCount)}</span>,
    },
    {
      key: 'avgRate',
      header: 'Avg Rate',
      sortable: true,
      className: 'text-right',
      render: (row) => (
        <span className="font-semibold text-status-success-foreground">
          {formatCurrency(row.avgRate)}
        </span>
      ),
    },
    {
      key: 'minRate',
      header: 'Min',
      sortable: true,
      className: 'text-right',
      render: (row) => <span className="text-muted-foreground">{formatCurrency(row.minRate)}</span>,
    },
    {
      key: 'maxRate',
      header: 'Max',
      sortable: true,
      className: 'text-right',
      render: (row) => <span className="text-muted-foreground">{formatCurrency(row.maxRate)}</span>,
    },
    {
      key: 'carriers',
      header: 'Carriers',
      sortable: true,
      className: 'text-right',
      render: (row) => <Badge variant="secondary">{row.carriers}</Badge>,
    },
  ];

  const expiringColumns: Column<ExpiringRate>[] = [
    {
      key: 'sku',
      header: 'SKU',
      sortable: true,
      render: (row) => <span className="font-mono text-sm font-medium">{row.sku}</span>,
    },
    {
      key: 'carrier',
      header: 'Carrier',
      render: (row) => (
        <div>
          <div className="font-medium text-sm">{row.carrier.name}</div>
          <div className="text-xs text-muted-foreground">{row.carrier.code}</div>
        </div>
      ),
    },
    {
      key: 'originPort',
      header: 'Lane',
      sortable: true,
      render: (row) => (
        <div className="flex items-center gap-2 text-sm">
          <span>{row.originPort}</span>

          <span>{row.destinationPort}</span>
        </div>
      ),
    },
    {
      key: 'baseRate',
      header: 'Rate',
      sortable: true,
      className: 'text-right',
      render: (row) => (
        <div>
          {/* The currency was already sitting underneath this figure while
              the figure itself was formatted as dollars. */}
          <div className="font-semibold">{formatCurrency(row.baseRate, row.currency)}</div>
        </div>
      ),
    },
    {
      key: 'validTo',
      header: 'Expires',
      sortable: true,
      className: 'text-right',
      render: (row) => (
        <span className="text-sm text-muted-foreground">{formatDate(row.validTo)}</span>
      ),
    },
    {
      key: 'daysUntilExpiry',
      header: 'Days Left',
      sortable: true,
      className: 'text-right',
      render: (row) => (
        <Badge
          variant="outline"
          className={
            row.daysUntilExpiry <= 7
              ? 'bg-status-destructive-subtle text-status-destructive-foreground'
              : row.daysUntilExpiry <= 14
                ? 'bg-status-warning-subtle text-status-warning-foreground'
                : 'bg-status-info-subtle text-status-info-foreground'
          }
        >
          {row.daysUntilExpiry}d
        </Badge>
      ),
    },
  ];

  return (
    <motion.main className="space-y-5" variants={stagger} initial="hidden" animate="show">
      <motion.div variants={fadeUp}>
        <PageHeader
          title="Rate Analytics"
          description="Contracted rate levels by carrier and lane, with spread and movement over time."
        />
      </motion.div>

      {/* KPIs */}
      <div className="mt-6">
        <KPIGrid columns={4}>
          <MetricCard
            label="Active Rates"
            value={formatNumber(summary?.activeRates || 0)}
            sentiment="success"
            icon={<FileText className="w-5 h-5" />}
            subtitle={`${formatNumber(summary?.totalRates || 0)} total`}
          />
          <MetricCard
            label="Expiring Soon"
            value={formatNumber(summary?.expiringRates || 0)}
            sentiment={(summary?.expiringRates || 0) > 0 ? 'warning' : 'success'}
            icon={<AlertTriangle className="w-5 h-5" />}
          />
          <MetricCard
            label="Contract Rates"
            value={formatNumber(summary?.contractRates || 0)}
            sentiment="info"
            icon={<FileText className="w-5 h-5" />}
          />
          <MetricCard
            label="Average Rate"
            value={formatCurrency(summary?.avgRate || 0)}
            sentiment="success"
            icon={<DollarSign className="w-5 h-5" />}
          />
        </KPIGrid>
      </div>

      {/* Charts Row */}
      <motion.div variants={fadeUp} className="grid grid-cols-1 lg:grid-cols-2 gap-6 mt-6">
        {/* Rate Trends Chart */}
        <Card>
          <CardHeader>
            <h3 className="text-base font-semibold text-foreground">
              <BarChart3 className="w-4 h-4" />
              Rate Trends (6 Months)
            </h3>
          </CardHeader>
          <CardContent>
            {trends.length > 0 ? (
              <div className="h-64">
                <ResponsiveContainer width="100%" height="100%">
                  <AreaChart data={trends}>
                    <CartesianGrid strokeDasharray="3 3" className="stroke-muted" />
                    <XAxis
                      dataKey="date"
                      tick={{ fontSize: 12 }}
                      tickFormatter={(v) =>
                        new Date(v).toLocaleDateString('en-US', { month: 'short' })
                      }
                    />
                    <YAxis
                      tick={{ fontSize: 12 }}
                      tickFormatter={(v) => `$${v.toLocaleString()}`}
                    />
                    <Tooltip
                      labelFormatter={(label) =>
                        new Date(label).toLocaleDateString('en-US', {
                          month: 'long',
                          year: 'numeric',
                        })
                      }
                      formatter={(value: number, name: string) => [
                        formatCurrency(value),
                        name === 'avgRate' ? 'Average' : name === 'minRate' ? 'Min' : 'Max',
                      ]}
                    />
                    <Legend />
                    <Area
                      type="monotone"
                      dataKey="maxRate"
                      name="Max"
                      fill="#EF444420"
                      stroke="#EF4444"
                      strokeWidth={1}
                    />
                    <Area
                      type="monotone"
                      dataKey="avgRate"
                      name="Average"
                      fill="#3B82F620"
                      stroke="#3B82F6"
                      strokeWidth={2}
                    />
                    <Area
                      type="monotone"
                      dataKey="minRate"
                      name="Min"
                      fill="#10B98120"
                      stroke="#10B981"
                      strokeWidth={1}
                    />
                  </AreaChart>
                </ResponsiveContainer>
              </div>
            ) : (
              <div className="h-64 flex items-center justify-center text-muted-foreground">
                No trend data available
              </div>
            )}
          </CardContent>
        </Card>

        {/* Rates by Mode Pie Chart */}
        <Card>
          <CardHeader>
            <h3 className="text-base font-semibold text-foreground">Distribution by Mode</h3>
          </CardHeader>
          <CardContent>
            {summary?.byMode && summary.byMode.length > 0 ? (
              <div className="h-64">
                <ResponsiveContainer width="100%" height="100%">
                  <PieChart>
                    <Pie
                      data={summary.byMode}
                      cx="50%"
                      cy="50%"
                      innerRadius={50}
                      outerRadius={80}
                      paddingAngle={2}
                      dataKey="count"
                      nameKey="mode"
                    >
                      {summary.byMode.map((entry, index) => (
                        <Cell
                          key={`cell-${index}`}
                          fill={MODE_COLORS[entry.mode] || COLORS[index % COLORS.length]}
                        />
                      ))}
                    </Pie>
                    <Tooltip
                      formatter={(value: number, name: string, props) => [
                        `${formatNumber(value)} (${formatCurrency(props.payload.avgRate)} avg)`,
                        name,
                      ]}
                    />
                    <Legend />
                  </PieChart>
                </ResponsiveContainer>
              </div>
            ) : (
              <div className="h-64 flex items-center justify-center text-muted-foreground">
                No data available
              </div>
            )}
          </CardContent>
        </Card>
      </motion.div>

      {/* Tabbed Analysis */}
      <Card>
        <CardContent className="pt-6">
          <Tabs value={activeTab} onValueChange={(v) => setFilter('tab', v)}>
            <div className="flex flex-wrap items-center justify-between gap-4 mb-6">
              <TabsList>
                <TabsTrigger value="carrier">By Carrier</TabsTrigger>
                <TabsTrigger value="lane">By Lane</TabsTrigger>
                <TabsTrigger value="expiring">Expiring</TabsTrigger>
              </TabsList>

              {activeTab !== 'expiring' && (
                <Select
                  value={modeFilter || 'ALL_MODES'}
                  onValueChange={(v) => setFilter('mode', v === 'ALL_MODES' ? undefined : v)}
                >
                  <SelectTrigger className="w-40">
                    <SelectValue placeholder="All Modes" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="ALL_MODES">All Modes</SelectItem>
                    <SelectItem value="TruckFTL">Truck FTL</SelectItem>
                    <SelectItem value="TruckLTL">Truck LTL</SelectItem>
                    <SelectItem value="Ocean">Ocean</SelectItem>
                    <SelectItem value="Air">Air</SelectItem>
                    <SelectItem value="Rail">Rail</SelectItem>
                    <SelectItem value="Intermodal">Intermodal</SelectItem>
                  </SelectContent>
                </Select>
              )}

              {activeTab === 'expiring' && (
                <div className="flex gap-2">
                  {EXPIRY_DAYS.map((d) => (
                    <Button
                      key={d.value}
                      variant={expiryDays === d.value ? 'default' : 'outline'}
                      size="sm"
                      onClick={() => setFilter('days', d.value.toString())}
                    >
                      {d.label}
                    </Button>
                  ))}
                </div>
              )}
            </div>

            {/* By Carrier Tab */}
            <TabsContent value="carrier">
              {loading ? (
                <div className="flex items-center justify-center py-12">
                  <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
                </div>
              ) : (
                <DataTable
                  pagination={false}
                  data={carrierStats}
                  columns={carrierColumns}
                  emptyTitle="No carrier data available"
                />
              )}
            </TabsContent>

            {/* By Lane Tab */}
            <TabsContent value="lane">
              {loading ? (
                <div className="flex items-center justify-center py-12">
                  <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
                </div>
              ) : (
                <DataTable
                  pagination={false}
                  data={laneStats}
                  columns={laneColumns}
                  emptyTitle="No lane data available"
                />
              )}
            </TabsContent>

            {/* Expiring Tab */}
            <TabsContent value="expiring">
              {loading ? (
                <div className="flex items-center justify-center py-12">
                  <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
                </div>
              ) : (
                <DataTable
                  pagination={false}
                  data={expiringRates}
                  columns={expiringColumns}
                  emptyTitle={`No rates expiring in the next ${expiryDays} days`}
                />
              )}
            </TabsContent>
          </Tabs>
        </CardContent>
      </Card>
    </motion.main>
  );
}
